import React from 'react'
import { Image, ImageStyle, TouchableOpacity, View } from 'react-native'
import { StyleService, useStyleSheet } from '@style/service'
import { Icon, Text } from '@components/base'
import { useNoraFileAttachments } from '@screens/Nora/context/NoraFileAttachmentsContext'

interface AttachmentsPreviewWithAddMoreProps {
  isAsking: boolean
}

export const AttachmentsPreviewWithAddMore = ({ isAsking }: AttachmentsPreviewWithAddMoreProps) => {
  const styles = useStyleSheet(themedStyle)

  const noraFileAttachments = useNoraFileAttachments()
  const attachments = noraFileAttachments?.attachments || []
  const hasUploadingAttachments = noraFileAttachments?.isUploading || false
  const attachmentLimitReached = !noraFileAttachments?.canAttachMore
  const isAttachmentActionDisabled = attachmentLimitReached || hasUploadingAttachments || isAsking

  const handleRemoveAttachment = (localId: string) => {
    noraFileAttachments?.removeAttachment(localId)
  }

  return (
    <View style={styles.inlineAttachmentsRow}>
      {attachments.map((attachment) => {
        const isImage = attachment.mimeType.startsWith('image/')

        return (
          <View key={attachment.localId} style={styles.previewCard}>
            {isImage ? (
              <Image
                source={{ uri: attachment.uri }}
                style={styles.imagePreview as ImageStyle}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.pdfPreview}>
                <Icon name="file-text" size={20} style={styles.pdfIcon} />
                <Text type="tiny" numberOfLines={2} lineSpacing="none" style={styles.pdfFileName}>
                  {attachment.fileName}
                </Text>
              </View>
            )}
            <TouchableOpacity
              accessibilityLabel={`Remove ${attachment.fileName}`}
              disabled={hasUploadingAttachments}
              onPress={() => handleRemoveAttachment(attachment.localId)}
              style={styles.removeButton}
            >
              <Icon name="x" size={10} weight="bold" style={styles.removeIcon} />
            </TouchableOpacity>
          </View>
        )
      })}

      <TouchableOpacity
        accessibilityLabel="Attach file"
        disabled={isAttachmentActionDisabled}
        onPress={noraFileAttachments!.openAttachmentPicker}
        style={[
          styles.attachmentAddTile,
          isAttachmentActionDisabled && styles.attachmentAddTileDisabled,
        ]}
      >
        <Icon name="plus" size={18} weight="bold" style={styles.attachmentAddTileIcon} />
      </TouchableOpacity>
    </View>
  )
}

const themedStyle = StyleService.create({
  inlineAttachmentsRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  previewCard: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.surface.base3',
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
  },
  pdfPreview: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 8,
    gap: 6,
  },
  pdfIcon: {
    color: 'theme.text.secondary',
  },
  pdfFileName: {
    textAlign: 'center',
    color: 'theme.text.secondary',
    width: '100%',
  },
  removeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'theme.primary.base',
    borderWidth: 1,
    borderColor: 'theme.primary.base',
  },
  removeIcon: {
    color: 'theme.solid.white',
  },
  attachmentAddTile: {
    width: 72,
    height: 72,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.surface.base2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  attachmentAddTileDisabled: {
    opacity: 0.4,
  },
  attachmentAddTileIcon: {
    color: 'theme.text.tertiary',
  },
})
